import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, TrendingUp, FileCheck, MessageSquare, Shield, Check, X, CreditCard, Edit2, Database, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [ndaLogs, setNdaLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('analytics');
  const [editingUser, setEditingUser] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan_name: '',
    billing_cycle: 'monthly',
    plan_start_date: '',
    plan_end_date: ''
  });

  // Plan options by role
  const planOptions = {
    founder: ['Founder Free', 'Founder Pro', 'Founder Elite'],
    investor: ['Investor Free', 'Investor Pro', 'Investor Premium'],
    cofounder: ['Co-Founder Free', 'Co-Founder Plus']
  };

  const fetchData = async () => {
    try {
      const [analyticsRes, usersRes, ndaRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/nda-logs`)
      ]);

      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setNdaLogs(ndaRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleVerifyInvestor = async (userId, verified) => {
    try {
      await axios.post(`${API}/admin/verify-investor/${userId}?verified=${verified}`);
      toast.success(`Investor ${verified ? 'verified' : 'unverified'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification status');
    }
  };

  const handleSeedDemoData = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/seed-demo-data`);
      const { results } = response.data;
      toast.success(
        `Demo data seeded! Founders: ${results.founders.created} created, ${results.founders.updated} updated. ` +
        `Investors: ${results.investors.created} created, ${results.investors.updated} updated. ` +
        `Co-founders: ${results.cofounders.created} created, ${results.cofounders.updated} updated.`
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleViewUser = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/admin/user/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleEditSubscription = (user) => {
    setEditingUser(user);
    setSubscriptionForm({
      plan_name: user.plan_name || (planOptions[user.role]?.[0] || ''),
      billing_cycle: user.billing_cycle || 'monthly',
      plan_start_date: user.plan_starts_at ? user.plan_starts_at.split('T')[0] : new Date().toISOString().split('T')[0],
      plan_end_date: user.plan_expires_at ? user.plan_expires_at.split('T')[0] : ''
    });
  };

  const handleSaveSubscription = async () => {
    if (!editingUser) return;

    try {
      await axios.post(`${API}/admin/update-user-subscription/${editingUser.user_id}`, {
        plan_name: subscriptionForm.plan_name,
        billing_cycle: subscriptionForm.billing_cycle,
        plan_start_date: subscriptionForm.plan_start_date ? new Date(subscriptionForm.plan_start_date).toISOString() : null,
        plan_end_date: subscriptionForm.plan_end_date ? new Date(subscriptionForm.plan_end_date).toISOString() : null
      });
      toast.success('Subscription updated successfully');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
  };

  const getStatusBadge = (user) => {
    const status = user.plan_status || 'free';
    if (status === 'active') return 'bg-green-500/20 text-green-400';
    if (status === 'expired') return 'bg-red-500/20 text-red-400';
    return 'bg-slate-700 text-blue-800';
  };

  const investors = users.filter(u => u.role === 'investor');

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-blue-900 mb-2">Admin Dashboard</h1>
          <p className="text-blue-700">Manage platform users and settings.</p>
        </div>

        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 text-sm font-medium">Founders</span>
                <Users className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-blue-900">{analytics.total_founders}</div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 text-sm font-medium">Investors</span>
                <TrendingUp className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-blue-900">{analytics.total_investors}</div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 text-sm font-medium">Co-founders</span>
                <Users className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-blue-900">{analytics.total_cofounders}</div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 text-sm font-medium">Matches</span>
                <Users className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-blue-900">{analytics.total_matches}</div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 text-sm font-medium">NDAs</span>
                <FileCheck className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-blue-900">{analytics.total_nda_accepts}</div>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-blue-800 text-sm font-medium">Active Chats</span>
                <MessageSquare className="text-blue-600" size={20} />
              </div>
              <div className="text-3xl font-bold text-blue-900">{analytics.active_conversations}</div>
            </Card>
          </div>
        )}

        {/* Seed Demo Data Section */}
        <Card className="bg-white border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                <Database className="text-blue-600" size={20} />
                Test Data Management
              </h3>
              <p className="text-blue-700 text-sm mt-1">
                Seed demo profiles for testing discovery, matching, and messaging flows.
              </p>
            </div>
            <Button
              onClick={handleSeedDemoData}
              disabled={seeding}
              className="btn-primary"
            >
              {seeding ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Seeding...
                </>
              ) : (
                <>
                  <Database className="mr-2" size={16} />
                  Seed Demo Data
                </>
              )}
            </Button>
          </div>
        </Card>

        <div className="mb-6">
          <div className="flex gap-4 border-b border-slate-300">
            <button
              onClick={() => setActiveTab('analytics')}
              data-testid="tab-analytics"
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'analytics' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-blue-700 hover:text-blue-800'
              }`}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('users')}
              data-testid="tab-users"
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'users' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-blue-700 hover:text-blue-800'
              }`}
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('investors')}
              data-testid="tab-investors"
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'investors' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-blue-700 hover:text-blue-800'
              }`}
            >
              Verify Investors ({investors.length})
            </button>
            <button
              onClick={() => setActiveTab('nda')}
              data-testid="tab-nda"
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'nda' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-blue-700 hover:text-blue-800'
              }`}
            >
              NDA Logs ({ndaLogs.length})
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              data-testid="tab-subscriptions"
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'subscriptions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-blue-700 hover:text-blue-800'
              }`}
            >
              <CreditCard size={16} className="inline mr-2" />
              Subscriptions
            </button>
          </div>
        </div>

        {activeTab === 'analytics' && analytics && (
          <Card className="bg-white border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-6">Platform Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-blue-700 text-sm mb-3">User Distribution</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Founders</span>
                    <span className="text-blue-900 font-semibold">{analytics.total_founders}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Investors</span>
                    <span className="text-blue-900 font-semibold">{analytics.total_investors}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Co-founders</span>
                    <span className="text-blue-900 font-semibold">{analytics.total_cofounders}</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-blue-700 text-sm mb-3">Platform Activity</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Total Matches</span>
                    <span className="text-blue-900 font-semibold">{analytics.total_matches}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">NDAs Signed</span>
                    <span className="text-blue-900 font-semibold">{analytics.total_nda_accepts}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Active Conversations</span>
                    <span className="text-blue-900 font-semibold">{analytics.active_conversations}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {activeTab === 'users' && (
          <Card className="bg-white border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-6">All Users</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-300 text-left">
                    <th className="pb-3 text-blue-700 font-medium">Name</th>
                    <th className="pb-3 text-blue-700 font-medium">Email</th>
                    <th className="pb-3 text-blue-700 font-medium">Role</th>
                    <th className="pb-3 text-blue-700 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.user_id} className="border-b border-slate-200">
                      <td className="py-3 text-blue-800">{user.name}</td>
                      <td className="py-3 text-blue-800">{user.email}</td>
                      <td className="py-3">
                        <span className="text-xs bg-slate-100 text-blue-800 px-2 py-1 rounded">
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3">
                        <Button
                          onClick={() => handleDeleteUser(user.user_id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <X size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {activeTab === 'investors' && (
          <Card className="bg-white border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-6">Investor Verification</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-300 text-left">
                    <th className="pb-3 text-blue-700 font-medium">Name</th>
                    <th className="pb-3 text-blue-700 font-medium">Email</th>
                    <th className="pb-3 text-blue-700 font-medium">Type</th>
                    <th className="pb-3 text-blue-700 font-medium">Status</th>
                    <th className="pb-3 text-blue-700 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => u.role === 'investor').map(user => (
                    <tr key={user.user_id} className="border-b border-slate-200">
                      <td className="py-3 text-blue-800">{user.name}</td>
                      <td className="py-3 text-blue-800">{user.email}</td>
                      <td className="py-3 text-blue-800">Investor</td>
                      <td className="py-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          user.verified ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {user.verified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleVerifyInvestor(user.user_id, true)}
                            size="sm"
                            className="btn-primary"
                            data-testid={`verify-${user.user_id}`}
                          >
                            <Check size={16} className="mr-1" />
                            Verify
                          </Button>
                          <Button
                            onClick={() => handleVerifyInvestor(user.user_id, false)}
                            size="sm"
                            className="btn-secondary"
                            data-testid={`unverify-${user.user_id}`}
                          >
                            <X size={16} className="mr-1" />
                            Revoke
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {activeTab === 'nda' && (
          <Card className="bg-white border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-6">NDA Acceptance Logs</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-300 text-left">
                    <th className="pb-3 text-blue-700 font-medium">Investor ID</th>
                    <th className="pb-3 text-blue-700 font-medium">Founder ID</th>
                    <th className="pb-3 text-blue-700 font-medium">IP Address</th>
                    <th className="pb-3 text-blue-700 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ndaLogs.map(log => (
                    <tr key={log.nda_id} className="border-b border-slate-200">
                      <td className="py-3 text-blue-800 font-mono text-xs">{log.investor_id.slice(0, 8)}...</td>
                      <td className="py-3 text-blue-800 font-mono text-xs">{log.founder_id.slice(0, 8)}...</td>
                      <td className="py-3 text-blue-700 text-sm">{log.ip_address || 'N/A'}</td>
                      <td className="py-3 text-blue-700 text-sm">
                        {new Date(log.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            {/* Subscription Edit Modal */}
            {editingUser && (
              <Card className="bg-white border border-slate-200 shadow-sm p-6 border-blue-600/50">
                <h2 className="text-xl font-bold text-blue-900 mb-6">
                  Edit Subscription: {editingUser.name}
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-blue-700 text-sm">Current Role</Label>
                    <Input 
                      value={editingUser.role} 
                      disabled 
                      className="input-field mt-1 bg-slate-100/50 capitalize" 
                    />
                  </div>
                  <div>
                    <Label className="text-blue-700 text-sm">Plan</Label>
                    <Select 
                      value={subscriptionForm.plan_name}
                      onValueChange={(value) => setSubscriptionForm({...subscriptionForm, plan_name: value})}
                    >
                      <SelectTrigger className="input-field mt-1">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {(planOptions[editingUser.role] || []).map(plan => (
                          <SelectItem key={plan} value={plan}>{plan}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-700 text-sm">Billing Cycle</Label>
                    <Select 
                      value={subscriptionForm.billing_cycle}
                      onValueChange={(value) => setSubscriptionForm({...subscriptionForm, billing_cycle: value})}
                    >
                      <SelectTrigger className="input-field mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-700 text-sm">Start Date</Label>
                    <Input 
                      type="date"
                      value={subscriptionForm.plan_start_date}
                      onChange={(e) => setSubscriptionForm({...subscriptionForm, plan_start_date: e.target.value})}
                      className="input-field mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-blue-700 text-sm">End Date (leave empty for free plans)</Label>
                    <Input 
                      type="date"
                      value={subscriptionForm.plan_end_date}
                      onChange={(e) => setSubscriptionForm({...subscriptionForm, plan_end_date: e.target.value})}
                      className="input-field mt-1" 
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button onClick={handleSaveSubscription} className="btn-primary">
                    <Check size={16} className="mr-2" />
                    Save Changes
                  </Button>
                  <Button onClick={handleCancelEdit} variant="ghost" className="text-blue-800">
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            {/* Subscriptions Table */}
            <Card className="bg-white border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-blue-900 mb-6">User Subscriptions</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-300 text-left">
                      <th className="pb-3 text-blue-700 font-medium">User</th>
                      <th className="pb-3 text-blue-700 font-medium">Role</th>
                      <th className="pb-3 text-blue-700 font-medium">Plan</th>
                      <th className="pb-3 text-blue-700 font-medium">Billing</th>
                      <th className="pb-3 text-blue-700 font-medium">Status</th>
                      <th className="pb-3 text-blue-700 font-medium">Expires</th>
                      <th className="pb-3 text-blue-700 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role !== 'admin').map(user => (
                      <tr key={user.user_id} className="border-b border-slate-200">
                        <td className="py-3">
                          <div>
                            <div className="text-blue-800">{user.name}</div>
                            <div className="text-blue-700 text-xs">{user.email}</div>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className="text-xs bg-slate-100 text-blue-800 px-2 py-1 rounded capitalize">
                            {user.role}
                          </span>
                        </td>
                        <td className="py-3 text-blue-800">{user.plan_name || 'Free'}</td>
                        <td className="py-3 text-blue-700 text-sm capitalize">{user.billing_cycle || 'monthly'}</td>
                        <td className="py-3">
                          <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(user)}`}>
                            {user.plan_status || 'free'}
                          </span>
                        </td>
                        <td className="py-3 text-blue-700 text-sm">
                          {user.plan_expires_at 
                            ? new Date(user.plan_expires_at).toLocaleDateString() 
                            : '—'
                          }
                        </td>
                        <td className="py-3">
                          <Button
                            onClick={() => handleEditSubscription(user)}
                            size="sm"
                            variant="ghost"
                            className="text-blue-500 hover:text-blue-300"
                          >
                            <Edit2 size={14} className="mr-1" />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
